import React, { useEffect, useRef } from 'react';
import { assetUrl } from '../utils/assets';
import { ChatDots, Bug, LockSimple, House, FileText } from '@phosphor-icons/react';
import './MenuDropdown.css';

interface MenuDropdownProps {
  isOpen: boolean;
  onClose: () => void;
  onFeedbackClick: () => void;
  onBugReportClick: () => void;
  onPrivacyPolicyClick: () => void;
  onTermsClick: () => void;
  onMainMenuClick?: () => void;
}

const DISCORD_URL = 'https://discord.gg/RM7zFmFzHQ';

const MenuDropdown: React.FC<MenuDropdownProps> = ({
  isOpen,
  onClose,
  onFeedbackClick,
  onBugReportClick,
  onPrivacyPolicyClick,
  onTermsClick,
  onMainMenuClick,
}) => {
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        onClose();
      }
    };

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen, onClose]);

  if (!isOpen) {
    return null;
  }

  const handleMenuItemClick = (callback: () => void) => {
    callback();
    onClose();
  };

  return (
    <div className="menu-dropdown" ref={dropdownRef}>
      {onMainMenuClick && (
        <>
          <button className="menu-item" onClick={() => handleMenuItemClick(onMainMenuClick)}>
            <span className="menu-icon"><House size={18} weight="duotone" /></span>
            <span className="menu-text">Main Menu</span>
          </button>
          <div className="menu-divider"></div>
        </>
      )}

      <button className="menu-item" onClick={() => handleMenuItemClick(onFeedbackClick)}>
        <span className="menu-icon"><ChatDots size={18} weight="duotone" /></span>
        <span className="menu-text">Give Feedback</span>
      </button>

      <button className="menu-item" onClick={() => handleMenuItemClick(onBugReportClick)}>
        <span className="menu-icon"><Bug size={18} weight="duotone" /></span>
        <span className="menu-text">Report a Bug</span>
      </button>

      <div className="menu-divider"></div>

      <div className="menu-section-title">Community</div>
      <a className="menu-item" href={DISCORD_URL} target="_blank" rel="noopener noreferrer" onClick={onClose}>
        <span className="menu-icon"><img src={assetUrl('/assets/icons/discord.svg')} alt="" className="menu-icon-img" /></span>
        <span className="menu-text">Discord</span>
      </a>

      <div className="menu-divider"></div>

      <button className="menu-item" onClick={() => handleMenuItemClick(onPrivacyPolicyClick)}>
        <span className="menu-icon"><LockSimple size={18} weight="duotone" /></span>
        <span className="menu-text">Privacy Policy</span>
      </button>

      <button className="menu-item" onClick={() => handleMenuItemClick(onTermsClick)}>
        <span className="menu-icon"><FileText size={18} weight="duotone" /></span>
        <span className="menu-text">Terms</span>
      </button>

      <div className="menu-disclaimer">
        Stories are AI-generated. Every playthrough is different.
      </div>
    </div>
  );
};

export default MenuDropdown;
